import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, YStack, createMqStyles, tokens } from '@fhs/ui';

import { useComboSelectionContext } from '../../../state/combo-selection';
import { MenuPickerProvider, useMenuPicker } from '../../../state/menu-picker';
import { GuideCurrentSelection } from '../current-selection';
import { ItemList } from '../item-list';
import { GuideLabeledDivider } from '../labeled-divider';
import { GuideSubheading } from '../subheading';
import { ItemSelectionParams } from '../types';

import { ItemDetailsContent } from './item-details-content';
import { ItemDetailsFooter } from './item-details-footer';

const ItemDetailsContainer = ({
  currentStep,
  onSaveItem,
}: {
  currentStep: any;
  onSaveItem: () => void;
}) => {
  const menuPicker = useMenuPicker();
  const { addOrUpdateSelectedPicker } = useComboSelectionContext();

  // Mount and save the picker object using current menu picker
  const handleSaveItem = useCallback(() => {
    const {
      query,
      slug: itemSlug,
      displayName,
      priceCents,
      instructions,
      selectedPickerAspectOptionId,
      selectedPickerAspectOption,
      defaultCustomizationSelections,
      userCustomizationSelections,
      resolvedCustomizationSelections,
      draftCustomizationSelections,
      pickerSelections,
    } = menuPicker;

    addOrUpdateSelectedPicker(currentStep.id, {
      id: query.data?.id,
      slug: itemSlug,
      displayName,
      priceCents,
      instructions,
      selectedPickerAspectOptionId,
      selectedPickerAspectOption,
      defaultCustomizationSelections,
      userCustomizationSelections,
      resolvedCustomizationSelections,
      draftCustomizationSelections,
      pickerSelections,
      image: query.data?.image,
      requiresCustomization: true,
    });

    onSaveItem();
  }, [addOrUpdateSelectedPicker, onSaveItem, menuPicker, currentStep]);

  return (
    <View style={{ flex: 1 }}>
      <ItemDetailsContent selectedPickerId={currentStep.id} />
      {!menuPicker.query.isLoading && <ItemDetailsFooter onSaveItem={handleSaveItem} />}
    </View>
  );
};

export const ItemContainer = ({
  selectedItemSlug,
  isCustomization,
  currentStep,
  onSaveItem,
  onSkip,
  onSelectItem,
  currentStepSelectedItemId,
}: {
  selectedItemSlug: string;
  isCustomization: boolean;
  currentStep: any; // TODO: specify type from use-guide - currentStep
  currentStepSelectedItemId: string;
  onSelectItem: (params: ItemSelectionParams) => void;
  onSaveItem: () => void;
  onSkip: () => void;
}) => {
  const { selectedPickers, isEditingPickers } = useComboSelectionContext();
  const mqStyles = useMqStyles();

  const selectedPicker = selectedPickers[currentStep.id];
  const showPreviousSelection = isEditingPickers && selectedPicker && !isCustomization;
  const previousSelectionTitle =
    showPreviousSelection && selectedPicker.requiresCustomization
      ? 'Edit Your Selection:'
      : 'Your Selection:';

  return (
    <View style={{ flex: 1 }}>
      <YStack
        style={[
          { flex: 11 },
          // customization screen already has horizontal padding built in
          !isCustomization && mqStyles.contentContainerPaddingHorizontal,
        ]}
      >
        {showPreviousSelection && (
          <YStack style={styles.editSelectionSection}>
            <GuideSubheading text={previousSelectionTitle} />
            <GuideCurrentSelection
              label={selectedPicker.displayName}
              image={selectedPicker.image}
              onEditPress={
                selectedPicker.requiresCustomization
                  ? () =>
                      // When editing, we want to open the product details page, regardless of the item having customization
                      onSelectItem({
                        itemId: selectedPicker.id,
                        itemSlug: selectedPicker.slug,
                        itemName: selectedPicker.displayName,
                        requiresCustomization: true,
                        image: selectedPicker.image,
                        price: selectedPicker.priceCents,
                      })
                  : undefined
              }
            />
            <GuideLabeledDivider style={styles.currentSelectionDivider} labelText="or" />
          </YStack>
        )}
        {isCustomization ? (
          <MenuPickerProvider slug={selectedItemSlug}>
            <ItemDetailsContainer currentStep={currentStep} onSaveItem={onSaveItem} />
          </MenuPickerProvider>
        ) : (
          <ItemList
            currentStep={currentStep}
            currentStepSelectedItemId={currentStepSelectedItemId}
            onSelectItem={onSelectItem}
          />
        )}
      </YStack>
      {showPreviousSelection && (
        <YStack style={styles.listEditFooter}>
          <Button type="outline" onPress={onSkip} style={styles.skipButton}>
            <Button.Text style={styles.skipButtonText}>Skip</Button.Text>
          </Button>
        </YStack>
      )}
    </View>
  );
};

const useMqStyles = createMqStyles({
  contentContainerPaddingHorizontal: {
    $gteDesktop: {
      paddingHorizontal: 28,
    },
  },
});

const styles = StyleSheet.create({
  editSelectionSection: {
    gap: 12,
    padding: 16,
  },
  currentSelectionDivider: {
    marginTop: 4,
  },
  listEditFooter: {
    backgroundColor: tokens.colors.$white,
    padding: 16,
    flex: 1,
    flexDirection: 'row',
    alignSelf: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: 52,
    borderTopWidth: 1,
    borderColor: tokens.colors.$black10,
  },
  skipButton: {
    flex: 1,
    fontSize: 18,
    height: 48,
  },
  skipButtonText: {
    fontSize: 18,
  },
});
